import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { PASSWORD_MIN_LENGTH } from '@/entities/viewer';
import { FormError, useFieldError, useServerErrors } from '@/shared/forms';
import { Button, TextField } from '@/shared/ui';

import { useRegister } from './api';
import styles from './AuthForm.module.css';
import { type RegisterValues, registerSchema } from './schemas';

const FIELDS = ['login', 'email', 'display_name', 'password'] as const;

export function RegisterForm({ onSuccess }: { onSuccess: () => void }) {
  const { t } = useTranslation();
  const fieldError = useFieldError();
  const registration = useRegister();
  const { register, handleSubmit, formState, setError } = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { login: '', email: '', display_name: '', password: '' },
  });
  const serverErrors = useServerErrors(setError, FIELDS, {
    LOGIN_TAKEN: 'login',
    EMAIL_TAKEN: 'email',
  });

  const onSubmit = handleSubmit((values) => {
    serverErrors.clear();
    registration.mutate(values, { onSuccess, onError: serverErrors.handle });
  });

  return (
    <form className={styles.form} onSubmit={(e) => void onSubmit(e)} noValidate>
      <FormError error={serverErrors.formError} />
      <TextField
        label={t('auth.login')}
        autoComplete="username"
        autoFocus
        hint={t('auth.loginHint')}
        error={fieldError(formState.errors.login?.message)}
        {...register('login')}
      />
      <TextField
        label={t('auth.email')}
        type="email"
        autoComplete="email"
        error={fieldError(formState.errors.email?.message)}
        {...register('email')}
      />
      <TextField
        label={t('auth.displayName')}
        autoComplete="name"
        error={fieldError(formState.errors.display_name?.message)}
        {...register('display_name')}
      />
      <TextField
        label={t('auth.password')}
        type="password"
        autoComplete="new-password"
        hint={t('auth.passwordHint', { count: PASSWORD_MIN_LENGTH })}
        error={fieldError(formState.errors.password?.message)}
        {...register('password')}
      />
      <Button type="submit" variant="primary" disabled={registration.isPending}>
        {t('auth.createAccount')}
      </Button>
      <p className={styles.switch}>
        {t('auth.haveAccount')} <Link to="/login">{t('auth.signIn')}</Link>
      </p>
    </form>
  );
}
